import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../Firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faSearch, faFilter, faCalendarDay, faChevronUp, faChevronDown, faBook, faUserGraduate, faCheckSquare, faChalkboardTeacher } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import './Payroll.css';

const Payroll = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [claims, setClaims] = useState([]);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('Pending'); // 'Pending', 'Confirmed', 'All'
    
    // UI State
    const [expandedTeachers, setExpandedTeachers] = useState({});
    const [expandedDays, setExpandedDays] = useState({});
    const [expandedStudents, setExpandedStudents] = useState({});
    
    // Submit state
    const [selectedClaims, setSelectedClaims] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showFinished, setShowFinished] = useState(false);

    const toggle = (setter, key) => { setter(prev => ({ ...prev, [key]: !prev[key] })); };

    const normalizeDay = (d) => {
        if (!d) return '';
        const l = String(d).toLowerCase();
        if (l.includes('mon') || l.includes('จัน')) return 'Monday';
        if (l.includes('tue') || l.includes('อัง') || l.includes('อังคาร')) return 'Tuesday';
        if (l.includes('wed') || l.includes('พุธ')) return 'Wednesday';
        if (l.includes('thu') || l.includes('พฤ')) return 'Thursday';
        if (l.includes('fri') || l.includes('ศุก')) return 'Friday';
        if (l.includes('sat') || l.includes('เสา')) return 'Saturday';
        if (l.includes('sun') || l.includes('อา')) return 'Sunday';
        return '';
    };

    const getDayNameFromDate = (dateStr) => {
        if (!dateStr) return '';
        let [d, m, y] = dateStr.split('/');
        if (!d || !m || !y) return '';
        y = parseInt(y);
        if (y > 2500) y -= 543;
        const dateObj = new Date(y, parseInt(m) - 1, parseInt(d));
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return isNaN(dateObj.getTime()) ? '' : days[dateObj.getDay()];
    };

    const parseBEDateToTimestamp = (dateStr) => {
        if (!dateStr) return 0;
        let [d, m, y] = dateStr.split('/');
        if (!d || !m || !y) return 0;
        y = parseInt(y);
        if (y > 2500) y -= 543;
        const dateObj = new Date(y, parseInt(m) - 1, parseInt(d));
        return isNaN(dateObj.getTime()) ? 0 : dateObj.getTime();
    };

    const fetchAttendanceData = async () => {
        setLoading(true);
        try {
            const [timeSheetSnap, schoolTimeSheetSnap, courseSnap, schoolCourseSnap, studentSnap] = await Promise.all([
                getDocs(collection(db, "time-sheet")),
                getDocs(collection(db, "school-timesheet")),
                getDocs(collection(db, "course-ID")),
                getDocs(collection(db, "school-course-ID")),
                getDocs(collection(db, "office-recipt"))
            ]);
            
            const courseMap = {};
            [...courseSnap.docs, ...schoolCourseSnap.docs].forEach(doc => {
                const d = doc.data();
                const id = String(d['course-ID'] || d['school-course-ID'] || doc.id).trim();
                
                const masterPeriods = [];
                if (d.period) {
                    if (Array.isArray(d.period)) {
                        d.period.forEach(p => { if (p.day) masterPeriods.push({ day: normalizeDay(p.day), time: p.time || '-' }); });
                    } else if (typeof d.period === 'object') {
                        let i = 1;
                        while (d.period[`day-${i}`] !== undefined) {
                            masterPeriods.push({ day: normalizeDay(d.period[`day-${i}`]), time: d.period[`time-${i}`] || '-' });
                            i++;
                        }
                    }
                } else if (d.day) {
                    masterPeriods.push({ day: normalizeDay(d.day), time: d.time || '-' });
                }

                const endRaw = String(d['course-end'] || '');
                courseMap[id] = { 
                    isHour: endRaw.includes('ชม') || endRaw.includes('ชั่วโมง'),
                    totalSessions: parseFloat(endRaw) || 0,
                    name: d['course'] || d['courseName'] || 'ไม่ระบุ',
                    periods: masterPeriods
                };
            });

            const studentMap = {};
            studentSnap.docs.forEach(doc => {
                const data = doc.data();
                const id = String(data['office-ID'] || doc.id).trim();
                const fullName = `${data['name'] || ''} ${data['surname'] || ''}`.trim();
                const nickname = data['nickname'] ? ` (${data['nickname']})` : '';
                studentMap[id] = { displayName: fullName + nickname };
            });

            const dayNameThai = { 'Monday': 'วันจันทร์', 'Tuesday': 'วันอังคาร', 'Wednesday': 'วันพุธ', 'Thursday': 'วันพฤหัสบดี', 'Friday': 'วันศุกร์', 'Saturday': 'วันเสาร์', 'Sunday': 'วันอาทิตย์' };

            const allClaims = [];
            const studentStats = {}; // { stKey: { attended: 0, confirmed: 0, totalRequired: 0 } }
            
            const processSource = (docs, type) => {
                docs.forEach(doc => {
                    const slot = doc.data();
                    const courseId = String(slot['courseID'] || slot['course-ID'] || '').trim();
                    const masterCourse = courseMap[courseId] || { isHour: false, totalSessions: 0, name: slot.course || slot['course-name'] || 'ไม่ระบุคอร์ส', periods: [] };
                    
                    let studentName = 'ไม่ระบุ';
                    let receiptId = '';
                    if (type === 'Office') {
                        receiptId = String(slot['reciptID'] || '').trim();
                        const stInfo = studentMap[receiptId];
                        studentName = stInfo ? stInfo.displayName : (slot['student-name'] || slot.name || 'ไม่ระบุนักเรียน');
                    } else {
                        studentName = slot.level ? `ชั้นเรียน ${slot.level}` : 'โรงเรียน';
                    }

                    const stKey = `${studentName}|${receiptId}`;
                    if (!studentStats[stKey]) {
                        studentStats[stKey] = { attended: 0, confirmed: 0, totalRequired: masterCourse.totalSessions };
                    }

                    const slotDayRaw = normalizeDay(slot.day || slot.Day);
                    const sTimeRaw = slot.time || slot['startTime'] || slot['start-time'] || '';
                    const eTimeRaw = slot.endTime || slot['end-time'] || '';
                    
                    const sources = [slot];
                    if (slot.timesheet) sources.push(slot.timesheet);

                    sources.forEach(source => {
                        Object.keys(source).forEach(key => {
                            if (key.startsWith('date-')) {
                                const idx = key.split('-')[1];
                                const check = source[`check-${idx}`];
                                if (check && check !== 'ยกคลาส' && check !== 'ไม่มา' && check !== 0 && check !== '0') {
                                    const val = parseFloat(check);
                                    const count = isNaN(val) ? 1 : val;
                                    studentStats[stKey].attended += count;
                                    
                                    const isConfirmed = source[`confirm-${idx}`] === 1 || source[`confirm-${idx}`] === '1' || source[`confirm-${idx}`] === true;
                                    if (isConfirmed) {
                                        studentStats[stKey].confirmed += count;
                                    }
                                }

                                const isSubmitted = source[`submit-${idx}`] === 1 || source[`submit-${idx}`] === '1' || source[`submit-${idx}`] === true;
                                if (isSubmitted && check && check !== 'ยกคลาส' && check !== 'ไม่มา' && check !== 0 && check !== '0') {
                                    const dateStr = source[`date-${idx}`];
                                    const teacher = source[`teacher-${idx}`] || slot.teacher || slot['teacher-name'] || 'ไม่ระบุครู';
                                    const isConfirmed = source[`confirm-${idx}`] === 1 || source[`confirm-${idx}`] === '1' || source[`confirm-${idx}`] === true;
                                    
                                    let actualDayEn = getDayNameFromDate(dateStr);
                                    let slotDayEn = slotDayRaw;
                                    if (!slotDayEn && masterCourse.periods.length > 0) {
                                        slotDayEn = masterCourse.periods[0].day;
                                    }
                                    if (!slotDayEn) slotDayEn = actualDayEn;

                                    let startTime = sTimeRaw;
                                    let endTime = eTimeRaw;
                                    
                                    if (!startTime && masterCourse.periods.length > 0) {
                                        const match = masterCourse.periods.find(p => p.day === actualDayEn) || masterCourse.periods[0];
                                        if (match) {
                                            const tParts = (match.time || '').replace(/\s/g, '').replace(/[.:]/g, ':').split(/[-–—/]/);
                                            startTime = tParts[0] || '';
                                            endTime = tParts[1] || '';
                                        }
                                    }

                                    let taughtTime = String(source[`time-${idx}`] || '').trim();
                                    if (!taughtTime || taughtTime === '-' || taughtTime === 'undefined') {
                                        taughtTime = (startTime && endTime && endTime !== '-') ? `${startTime} - ${endTime}` : sTimeRaw;
                                    }

                                    const paydate = source[`paydate-${idx}`] || source.timesheet?.[`paydate-${idx}`] || '';

                                    allClaims.push({
                                        id: `${type}-${doc.id}-${idx}`,
                                        sourceDocId: doc.id,
                                        sourceIndex: idx,
                                        sourceType: type,
                                        date: dateStr,
                                        timestamp: parseBEDateToTimestamp(dateStr),
                                        check: check,
                                        teacher: typeof teacher === 'string' ? teacher.trim() : 'ไม่ระบุ',
                                        student: studentName,
                                        stKey,
                                        receiptId: receiptId,
                                        course: masterCourse.name,
                                        isHour: masterCourse.isHour,
                                        isSubmitted,
                                        isConfirmed,
                                        paydate,
                                        slotDay: dayNameThai[slotDayEn] || slotDayEn || 'Unknown',
                                        startTime: startTime || '-',
                                        endTime: endTime || '-',
                                        taughtTime: taughtTime || '-'
                                    });
                                }
                            }
                        });
                    });
                });
            };
            
            processSource(timeSheetSnap.docs, 'Office');
            processSource(schoolTimeSheetSnap.docs, 'School');
            
            // Attach stats to claims
            const finalClaims = allClaims.map(c => {
                const stats = studentStats[c.stKey];
                const isFinished = stats.totalRequired > 0 && stats.attended >= stats.totalRequired;
                const isAllPaid = isFinished && stats.confirmed >= stats.attended;
                return { ...c, isFinished, isAllPaid };
            });

            setClaims(finalClaims);
        } catch (error) {
            console.error("Error fetching payroll claims:", error);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchAttendanceData(); }, []);

    const groupedData = useMemo(() => {
        const filtered = claims.filter(c => {
            const matchesSearch = c.teacher.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                  c.student.toLowerCase().includes(searchTerm.toLowerCase());
            
            let matchesStatus = false;
            if (statusFilter === 'Pending') matchesStatus = c.isSubmitted && !c.isConfirmed;
            if (statusFilter === 'Confirmed') matchesStatus = c.isSubmitted && c.isConfirmed;
            if (statusFilter === 'All') matchesStatus = c.isSubmitted; 

            const matchesFinished = showFinished ? true : !c.isAllPaid;

            return matchesSearch && matchesStatus && matchesFinished;
        });

        const dayToWeight = { 'วันจันทร์': 1, 'วันอังคาร': 2, 'วันพุธ': 3, 'วันพฤหัสบดี': 4, 'วันศุกร์': 5, 'วันเสาร์': 6, 'วันอาทิตย์': 7 };

        const tGroups = {};
        filtered.forEach(c => {
            const tr = c.teacher;
            if (!tGroups[tr]) tGroups[tr] = { name: tr, totalSessions: 0, totalHours: 0, days: {} };

            const day = c.slotDay;
            if (!tGroups[tr].days[day]) tGroups[tr].days[day] = { name: day, totalSessions: 0, totalHours: 0, students: {} };
            
            const stKey = `${c.student}|${c.receiptId}`;
            if (!tGroups[tr].days[day].students[stKey]) {
                tGroups[tr].days[day].students[stKey] = {
                    name: c.student,
                    receiptId: c.receiptId,
                    totalStudentCount: 0,
                    sessions: [],
                    isHour: c.isHour,
                    course: c.course // Primary course name
                };
            }

            tGroups[tr].days[day].students[stKey].sessions.push(c);

            const val = parseFloat(c.check);
            const count = isNaN(val) ? 1 : val;

            tGroups[tr].days[day].students[stKey].totalStudentCount += count;
            
            if (c.isHour) {
                tGroups[tr].totalHours += count;
                tGroups[tr].days[day].totalHours += count;
            } else {
                tGroups[tr].totalSessions += count;
                tGroups[tr].days[day].totalSessions += count;
            }
        });

        const sortedTeachers = Object.values(tGroups).map(tGroup => ({
            ...tGroup,
            days: Object.values(tGroup.days).map(day => ({
                ...day,
                students: Object.values(day.students).map(st => {
                    st.sessions.sort((a, b) => b.timestamp - a.timestamp);
                    return st;
                }).sort((a, b) => a.name.localeCompare(b.name))
            })).sort((a, b) => (dayToWeight[a.name] || 99) - (dayToWeight[b.name] || 99))
        })).sort((a, b) => a.name.localeCompare(b.name));

        return sortedTeachers;
    }, [claims, searchTerm, statusFilter, showFinished]);

    const handleToggleClaim = (claimId) => {
        if (isSubmitting) return;
        setSelectedClaims(prev => {
            const next = { ...prev };
            if (next[claimId]) delete next[claimId];
            else next[claimId] = true;
            return next;
        });
    };

    const handleConfirmForStudent = async (studentData) => {
        // Admin only confirms unconfirmed records
        const studentSelected = studentData.sessions.filter(s => selectedClaims[s.id] && !s.isConfirmed);
        
        if (studentSelected.length === 0) {
            Swal.fire('แจ้งเตือน', 'กรุณาเลือกรายการที่รออนุมัติอย่างน้อย 1 รายการ', 'info');
            return;
        }

        const result = await Swal.fire({
            title: 'ยืนยันการอนุมัติจ่ายเงิน?',
            text: `ต้องการรับรองยอดสอนจำนวน ${studentSelected.length} รายการ สำหรับนักเรียน ${studentData.name} ใช่หรือไม่?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#cbd5e1',
            confirmButtonText: 'ใช่, อนุมัติเบิกจ่าย',
            cancelButtonText: 'ยกเลิก'
        });

        if (!result.isConfirmed) return;

        setIsSubmitting(true);
        try {
            const now = new Date();
            const dd = String(now.getDate()).padStart(2, '0');
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const yyyy = now.getFullYear() + 543;
            const todayStr = `${dd}/${mm}/${yyyy}`;

            const updatesByDoc = {};
            studentSelected.forEach(s => {
                const collName = s.sourceType === 'Office' ? 'time-sheet' : 'school-timesheet';
                const key = `${collName}/${s.sourceDocId}`;
                if (!updatesByDoc[key]) updatesByDoc[key] = { coll: collName, id: s.sourceDocId, fields: {} };
                
                updatesByDoc[key].fields[`timesheet.confirm-${s.sourceIndex}`] = 1;
                updatesByDoc[key].fields[`timesheet.paydate-${s.sourceIndex}`] = todayStr;
            });

            for (const key of Object.keys(updatesByDoc)) {
                const item = updatesByDoc[key];
                const docRef = doc(db, item.coll, item.id);
                await updateDoc(docRef, item.fields);
            }

            Swal.fire('สำเร็จ', 'อนุมัติการเบิกจ่ายเรียบร้อยแล้ว', 'success');
            
            setSelectedClaims(prev => {
                const next = { ...prev };
                studentSelected.forEach(s => delete next[s.id]);
                return next;
            });

            fetchAttendanceData();
        } catch (error) {
            console.error("Error confirming claims:", error);
            Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="payroll-section">
            <div className="sa-header">
                <img 
                    src="/Wisdom-Logo.png" 
                    alt="Wisdom Logo" 
                    className="payroll-logo" 
                    onClick={() => navigate('/main')}
                />
                <h2><FontAwesomeIcon icon={faFileInvoiceDollar} /> Payroll System (ระบบจ่ายเงินเดือน)</h2>

                <div className="sa-filters-compact">
                    <div className="sa-search-bar">
                        <FontAwesomeIcon icon={faSearch} className="sa-search-icon" />
                        <input type="text" placeholder="พิมพ์ชื่อคุณครู หรือนักเรียน..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <div className="sa-filter-group">
                        <div className="filter-item-small">
                            <label><FontAwesomeIcon icon={faFilter} /> สถานะ:</label>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="Pending">รออนุมัติ (Pending)</option>
                                <option value="Confirmed">จ่ายแล้ว (Confirmed)</option>
                                <option value="All">ทั้งหมด (ขอยื่นแล้ว)</option>
                            </select>
                        </div>
                        <div className="filter-item-small filter-checkbox-item" style={{ marginLeft: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input 
                                type="checkbox" 
                                id="showFinished" 
                                checked={showFinished} 
                                onChange={(e) => setShowFinished(e.target.checked)} 
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <label htmlFor="showFinished" style={{ cursor: 'pointer', fontSize: '0.9rem', color: '#475569', fontWeight: '600' }}>
                                แสดงจบคอร์สที่จ่ายครบแล้ว
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="sa-loading"><div className="loader-small"></div><p>กำลังดึงข้อมูลเข้าสู่ระบบ Payroll...</p></div>
            ) : (
                <div className="sa-4level-list animate-fade-in">
                    {groupedData.length > 0 ? groupedData.map((tGroup, tIdx) => (
                        <div key={tIdx} className={`sa-teacher-group ${expandedTeachers[tGroup.name] ? 'is-expanded' : ''}`}>
                            
                            {/* Level 1: Teacher */}
                            <div className="sa-teacher-header" onClick={() => toggle(setExpandedTeachers, tGroup.name)}>
                                <div className="teacher-info">
                                    <div className="teacher-icon"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                                    <span className="teacher-name">{tGroup.name}</span>
                                    {tGroup.totalSessions > 0 && <span className="teacher-total-badge">{tGroup.totalSessions} คาบ</span>}
                                    {tGroup.totalHours > 0 && <span className="teacher-total-badge">{tGroup.totalHours} ชั่วโมง</span>}
                                </div>
                                <FontAwesomeIcon icon={expandedTeachers[tGroup.name] ? faChevronUp : faChevronDown} className="teacher-expand" />
                            </div>

                            {expandedTeachers[tGroup.name] && (
                                <div className="sa-teacher-content animate-slide-down">
                                    {/* Level 2: Day */}
                                    {tGroup.days.map((day, dIdx) => {
                                        const dayKey = `${tGroup.name}-${day.name}`;
                                        return (
                                        <div key={dIdx} className={`sa-day-group ${expandedDays[dayKey] ? 'is-expanded' : ''}`}>
                                            <div className="sa-day-header" onClick={() => toggle(setExpandedDays, dayKey)}>
                                                <div className="day-info">
                                                    <div className="day-icon"><FontAwesomeIcon icon={faCalendarDay} /></div>
                                                    <span className="day-name">{day.name}</span>
                                                    {day.totalSessions > 0 && <span className="day-total-badge">{day.totalSessions} คาบ</span>}
                                                    {day.totalHours > 0 && <span className="day-total-badge">{day.totalHours} ชั่วโมง</span>}
                                                </div>
                                                <FontAwesomeIcon icon={expandedDays[dayKey] ? faChevronUp : faChevronDown} className="day-expand" />
                                            </div>

                                            {expandedDays[dayKey] && (
                                                <div className="sa-day-content animate-slide-down">
                                                    <div className="sa-student-list-container" style={{ marginTop: '15px' }}>
                                                        {/* Level 4: Student (Now Level 3) */}
                                                        {day.students.map((st, stIdx) => {
                                                            const stKey = `${dayKey}-${st.name}-${st.receiptId}`;
                                                            return (
                                                                <div key={stIdx} className={`sa-st-card-nested ${expandedStudents[stKey] ? 'is-expanded' : ''}`}>
                                                                    <div className="st-header" onClick={() => toggle(setExpandedStudents, stKey)}>
                                                                        <div className="st-name-block">
                                                                            <span className="st-name">
                                                                                <FontAwesomeIcon icon={faUserGraduate} /> {st.name}
                                                                                <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#64748b', marginLeft: '10px' }}>
                                                                                    <FontAwesomeIcon icon={faBook} /> {st.course}
                                                                                </span>
                                                                            </span>
                                                                            {st.receiptId && <span className="st-receipt">ใบเสร็จ: {st.receiptId}</span>}
                                                                        </div>
                                                                        <div className="st-info-meta">
                                                                            <span className="st-total">{st.totalStudentCount} {st.isHour ? 'ชั่วโมง' : 'คาบ'}</span>
                                                                            <FontAwesomeIcon icon={expandedStudents[stKey] ? faChevronUp : faChevronDown} fontSize="0.7rem" color="#cbd5e1" />
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    {/* Level 5: Sessions Checkbox */}
                                                                    {expandedStudents[stKey] && (
                                                                        <div className="st-logs animate-slide-down">
                                                                            <div className="table-responsive-micro">
                                                                                <table className="sa-micro-table">
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th style={{ width:'40px', textAlign:'center' }}>เลือก</th>
                                                                                            <th>วันที่สอน</th>
                                                                                            <th>เวลาเรียน</th>
                                                                                            <th>วิชา</th>
                                                                                            <th>เวลาบันทึก</th>
                                                                                            <th>สถานะเข้าเรียน</th>
                                                                                            <th>ครูผู้สอน</th>
                                                                                            <th>สถานะจ่ายเงิน</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {st.sessions.map((s, sessIdx) => (
                                                                                            <tr key={sessIdx}>
                                                                                                <td style={{textAlign:'center'}}>
                                                                                                    <input 
                                                                                                        type="checkbox" 
                                                                                                        className="claim-checkbox"
                                                                                                        checked={!!selectedClaims[s.id]}
                                                                                                        onChange={() => handleToggleClaim(s.id)}
                                                                                                        disabled={s.isConfirmed || isSubmitting}
                                                                                                    />
                                                                                                </td>
                                                                                                <td>{s.date}</td>
                                                                                                <td>{s.startTime} - {s.endTime}</td>
                                                                                                <td>{s.course}</td>
                                                                                                <td>{s.taughtTime}</td>
                                                                                                <td><span className="badge-present-micro">{s.check}</span></td>
                                                                                                <td>{s.teacher}</td>
                                                                                                <td>
                                                                                                    {s.isConfirmed ? 
                                                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                                            <span className="badge-confirmed">อนุมัติแล้ว</span>
                                                                                                            {s.paydate && <span style={{fontSize:'0.8rem', color:'#64748b', fontWeight: '600'}}>{s.paydate}</span>}
                                                                                                        </div>
                                                                                                        : 
                                                                                                        <span className="badge-pending">รออนุมัติ</span>
                                                                                                    }
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>

                                                                            {st.sessions.some(s => !s.isConfirmed) && (
                                                                                <div className="st-action-bar">
                                                                                    <button 
                                                                                        className="btn-submit-selected"
                                                                                        onClick={() => handleConfirmForStudent(st)}
                                                                                        disabled={isSubmitting || !st.sessions.some(s => selectedClaims[s.id] && !s.isConfirmed)}
                                                                                    >
                                                                                        <FontAwesomeIcon icon={faCheckSquare} /> 
                                                                                        {isSubmitting ? ' กำลังบันทึก...' : ' อนุมัติยอดสอน (Confirm Payroll)'}
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )})}
                                </div>
                            )}
                        </div>
                    )) : (
                        <div className="sa-no-data-card">ไม่มียอดสอนที่รออนุมัติเบิกจ่ายในระบบ</div>
                    )}
                </div>
            )}
        </section>
    );
};

export default Payroll;
